import { useState } from 'react';
import { sortBy } from 'lodash';
import { toast } from 'react-hot-toast';
import { gql, useMutation } from '@apollo/client';
import auth from '@client/components/auth';
import ajax from '../../../../components/ajax';
import { AnyFn, TMap } from '../../../../new/helpers/types';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { toLiteWorkout } from '@client/new/pages/collection/workouts/workouts.hooks';
import {
  LiteWorkout,
  RawWorkout,
} from '@client/new/pages/collection/workouts/workouts.domain';
import { getWorkoutDeepLink } from '@client/new/pages/collection/workouts/workout.query';
import {
  ProgramWorkoutsActions,
  buildProgramWorkoutsTable,
} from './programWorkoutsTable';

// The program fields consumed by the workouts view. Sourced from the (heavily
// nullable) generated Program plus its merged published workouts.
export type WorkoutProgram = {
  id: number;
  name: string;
  type: number | null;
  isVisible: boolean | null;
  isLocked: boolean | null;
  accessType: string | null;
  exclusiveProgramMetadata: { price: number | null } | null;
  color: string | null;
  contentStructure: string | null;
  workoutLimit: number | null;
  workouts: RawWorkout[];
};

export type ProgramWorkoutsProps = {
  program: WorkoutProgram;
  influencerId: number;
  // Resolves once the new order is persisted; rejects so the dropped position
  // can be rolled back.
  onReorder: (programId: number, workoutIds: number[]) => Promise<unknown>;
  onChange?: AnyFn;
};

export type ProgramWorkoutsVM = {
  isEmpty: boolean;
  table:
    | { type: 'single'; props: SmartTableProps<number> }
    | {
        type: 'limited';
        limit: number;
        editUrl: string;
        visible: SmartTableProps<number>;
        hidden: SmartTableProps<number>;
      };
  linkModal: { isOpen: boolean; url: string; onClose: () => void };
};

const UPDATE_WORKOUT_NAME = gql`
  mutation UpdateWorkoutName($workoutId: Int!, $name: String!) {
    updateWorkoutName(workoutId: $workoutId, name: $name) {
      id
      name
    }
  }
`;

// Mirrors WorkoutListItem's removeWeekDay: strips a leading W/D prefix so a new
// one can be prepended when the week/day selects change.
const removeWeekDay = (title: string) =>
  title.replace(/^(W\d+|D\d+|W\d+\/D\d+) /, '');

// Rebuilds a workout name from the selected week/day, matching the legacy
// WorkoutWeekDaySelect rules: `W{n}/D{m}`, `W{n}` or `D{m}` prefix + base title.
const buildWorkoutName = (
  orgTitle: string,
  week: string | undefined,
  day: string | undefined,
) => {
  const base = removeWeekDay(orgTitle);
  const prefix = week && day ? `${week}/${day}` : week ?? day ?? '';
  return prefix ? `${prefix} ${base}` : base;
};

// Applies the locally dropped order on top of the server order, so a row stays
// where it was dropped while the reorder request is in flight. Skipped when the
// override no longer describes exactly the current workouts (one was archived,
// duplicated, published...) - the server order wins then.
const applyOrder = (
  workouts: LiteWorkout[],
  order: number[] | null,
): LiteWorkout[] => {
  if (!order || order.length !== workouts.length) return workouts;
  const positions: TMap<number, number> = order.reduce(
    (acc, id, i) => ({ ...acc, [id]: i }),
    {} as TMap<number, number>,
  );
  if (workouts.some((w) => positions[w.workoutId] === undefined))
    return workouts;
  return sortBy(workouts, (w) => positions[w.workoutId]);
};

export const useProgramWorkouts = (
  props: ProgramWorkoutsProps,
): ProgramWorkoutsVM => {
  const { program, influencerId, onReorder } = props;
  const onChange = props.onChange ?? (() => undefined);

  const [linkModal, setLinkModal] = useState({ isOpen: false, url: '' });
  const [droppedOrder, setDroppedOrder] = useState<number[] | null>(null);
  const [updateWorkoutName] = useMutation(UPDATE_WORKOUT_NAME);

  const isAdminOrModerator = auth.isAdministrator() || auth.isModerator();
  const readonly = !isAdminOrModerator && Boolean(program.isLocked);

  const workouts: LiteWorkout[] = applyOrder(
    sortBy(
      program.workouts.filter((w) => w.publishedAt && w.isVisible),
      (w) => w.programIndex,
    ).map((w) => toLiteWorkout(w, 'published')),
    droppedOrder,
  );

  const byId: TMap<number, LiteWorkout> = workouts.reduce(
    (acc, w) => ({ ...acc, [w.workoutId]: w }),
    {} as TMap<number, LiteWorkout>,
  );

  const rename = (workoutId: number, week?: string, day?: string) => {
    const workout = byId[workoutId];
    if (!workout) return;
    const name = buildWorkoutName(workout.orgTitle, week, day);
    updateWorkoutName({ variables: { workoutId, name } }).then(onChange);
  };

  const actions: ProgramWorkoutsActions = {
    onReorder: (workoutIds) => {
      setDroppedOrder(workoutIds);
      onReorder(program.id, workoutIds).catch(() => {
        setDroppedOrder(null);
        toast.error('Could not save the new order. Please try again.');
      });
    },
    onDuplicate: (workoutId) => {
      if (confirm('Are you sure you want to *clone* this Workout?'))
        ajax.cloneWorkout(workoutId).then(onChange);
    },
    onArchive: (workoutId) => {
      if (confirm('Are you sure you want to *archive* this Workout?'))
        ajax.archiveWorkout(workoutId).then(onChange);
    },
    onCopyLink: (workoutId) => {
      getWorkoutDeepLink(workoutId).then((data) =>
        setLinkModal({ isOpen: true, url: data.url }),
      );
    },
    onSetWeek: ({ workoutId, newWeek, day }) => rename(workoutId, newWeek, day),
    onSetDay: ({ workoutId, newDay, week }) => rename(workoutId, week, newDay),
  };

  const workoutsStructure =
    program.contentStructure === 'dayweek' ? 'week/day' : 'series';

  const commonParams = {
    influencerId,
    programName: program.name,
    workouts,
    workoutsStructure,
    readonly,
    isProgramEditable: isAdminOrModerator || !program.isLocked,
    isWorkoutArchiveEnabled: program.accessType === 'premium',
    actions,
  } as const;

  const isEmpty = program.workouts.length === 0;
  const linkModalVM = {
    ...linkModal,
    onClose: () => setLinkModal({ isOpen: false, url: '' }),
  };

  const workoutLimit: number = program.workoutLimit ?? 0;

  if (workoutLimit > 0) {
    const toStandard = (slice: LiteWorkout[]): SmartTableProps<number> =>
      buildProgramWorkoutsTable(
        { ...commonParams, workouts: slice },
        { draggable: false },
      );

    return {
      isEmpty,
      linkModal: linkModalVM,
      table: {
        type: 'limited',
        limit: workoutLimit,
        editUrl: `/admin/${influencerId}/category/${program.id}/edit#workout-limit`,
        visible: toStandard(workouts.slice(0, workoutLimit)),
        hidden: toStandard(workouts.slice(workoutLimit)),
      },
    };
  }

  return {
    isEmpty,
    linkModal: linkModalVM,
    table: {
      type: 'single',
      props: buildProgramWorkoutsTable(commonParams, {
        draggable: !readonly && workoutsStructure === 'series',
      }),
    },
  };
};
